import { z } from 'zod';
/**
 * Phase 16 (16-2) — public marketing contact form + operator admin read.
 * First schema file in the `marketing/` resource dir (Convention 06). Backs the
 * public `POST /api/v1/contact` and the operator `GET /admin/contact-submissions`.
 */
/** Public submission payload. `_botField` is a honeypot — humans leave it empty;
 *  a non-empty value is silently dropped server-side. */
export const ContactSubmissionRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
    email: z.string().trim().email().max(254),
    subject: z.string().trim().min(1).max(160),
    message: z.string().trim().min(1).max(5000),
    /** Honeypot — must be absent/empty for a genuine submission. */
    _botField: z.string().max(200).optional(),
    /** Cloudflare Turnstile token — human-verification; checked by TurnstileGuard.
     *  Optional so dev/e2e (no site key) still submit; enforced when a key is set. */
    turnstileToken: z.string().max(4096).optional(),
});
/** Identical shape whether the submission was recorded or honeypot-dropped, so
 *  bots can't probe. `reference` is the submission id (null on honeypot drop). */
export const ContactSubmissionResponseSchema = z.object({
    ok: z.literal(true),
    reference: z.string().uuid().nullable(),
});
// ── operator admin read ──────────────────────────────────────────────────────
export const ContactSubmissionStatusSchema = z.enum(['NEW', 'HANDLED']);
export const AdminContactSubmissionSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    email: z.string(),
    subject: z.string(),
    message: z.string(),
    status: ContactSubmissionStatusSchema,
    handledAt: z.string().nullable(),
    replyCount: z.number().int().nonnegative(),
    createdAt: z.string(),
});
/** One operator reply in a submission's thread. */
export const AdminContactSubmissionReplySchema = z.object({
    id: z.string().uuid(),
    body: z.string(),
    replyToEmail: z.string(),
    sentByName: z.string().nullable(),
    sentAt: z.string(),
});
/** Full detail for the triage modal: the submission + its reply thread. */
export const AdminContactSubmissionDetailSchema = AdminContactSubmissionSchema.extend({
    handledByName: z.string().nullable(),
    replies: z.array(AdminContactSubmissionReplySchema),
});
export const AdminContactSubmissionListQuerySchema = z.object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(25),
    /** Triage filter chips. Defaults to `open` (the operator's to-do). */
    status: z.enum(['open', 'handled', 'all']).default('open'),
    /** Free-text match across name / email / subject / message. */
    search: z.string().trim().max(200).optional(),
});
export const AdminContactSubmissionListResponseSchema = z.object({
    items: z.array(AdminContactSubmissionSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
/** Operator in-app reply. `replyToEmail` defaults to the operator's profile
 *  email client-side but is editable, so the submitter's follow-up reaches a
 *  chosen inbox. */
export const AdminContactSubmissionReplyRequestSchema = z.object({
    body: z.string().trim().min(1).max(5000),
    replyToEmail: z.string().trim().email().max(254),
});
/** How many submissions the Purge button would delete (older than the cutoff). */
export const AdminContactSubmissionPurgeableSchema = z.object({
    count: z.number().int().nonnegative(),
    olderThanMonths: z.number().int().positive(),
});
export const AdminContactSubmissionPurgeResponseSchema = z.object({
    deletedCount: z.number().int().nonnegative(),
});
/** Generic operator-facing OK for mark-handled / delete. */
export const AdminContactSubmissionOkResponseSchema = z.object({ ok: z.literal(true) });
//# sourceMappingURL=contact.schema.js.map